import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { apiGet, tokenStore } from '@/lib/api';
import { useT } from '@/lib/i18n';

const REASONS: Record<string, string> = {
  cancelled: 'Sign-in was cancelled.',
  expired: 'That sign-in link expired. Please try again.',
  failed: 'Google or Microsoft did not confirm your account. Please try again.',
  no_account: 'There is no DovoCRM account with that email. Ask your administrator for an invitation.',
  inactive: 'This account is not active. Contact your administrator.',
  locked: 'This workspace is suspended.',
  two_factor: 'Your account uses two-factor authentication — sign in with your password and code.',
  not_configured: 'That sign-in option is not set up yet.',
};

/** "Continue with Google / Microsoft" on the sign-in page, when set up. */
export function SsoButtons() {
  const t = useT();
  const [params] = useSearchParams();
  const { data } = useQuery({ queryKey: ['sso', 'providers'], queryFn: () => apiGet<{ google: boolean; microsoft: boolean }>('/public/sso/providers'), staleTime: Infinity, retry: false });
  const reason = params.get('sso');
  if (!data?.google && !data?.microsoft && !reason) return null;
  const base = (import.meta.env.VITE_API_URL as string | undefined) ?? '/api/v1';
  return (
    <div className="space-y-3">
      {reason && <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-500/10 dark:text-amber-200">{t(REASONS[reason] ?? 'Sign-in did not complete.')}</p>}
      {data?.google && (
        <a href={`${base}/public/sso/google/start`} className="flex h-11 w-full items-center justify-center gap-2.5 rounded-md border bg-card text-sm font-semibold transition-colors hover:bg-accent">
          <svg viewBox="0 0 48 48" className="h-5 w-5" aria-hidden>
            <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
            <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
            <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
            <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
          </svg>
          {t('Continue with Google')}
        </a>
      )}
      {data?.microsoft && (
        <a href={`${base}/public/sso/microsoft/start`} className="flex h-11 w-full items-center justify-center gap-2.5 rounded-md border bg-card text-sm font-semibold transition-colors hover:bg-accent">
          <svg viewBox="0 0 23 23" className="h-4 w-4" aria-hidden>
            <path fill="#f35325" d="M1 1h10v10H1z" />
            <path fill="#81bc06" d="M12 1h10v10H12z" />
            <path fill="#05a6f0" d="M1 12h10v10H1z" />
            <path fill="#ffba08" d="M12 12h10v10H12z" />
          </svg>
          {t('Continue with Microsoft')}
        </a>
      )}
      {(data?.google || data?.microsoft) && (
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span className="h-px flex-1 bg-border" />
          {t('or')}
          <span className="h-px flex-1 bg-border" />
        </div>
      )}
    </div>
  );
}

/** /sso/callback#access=…&refresh=… — keeps the tokens and opens the app. */
export function SsoCallbackPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  useEffect(() => {
    const fragment = new URLSearchParams(window.location.hash.slice(1));
    const access = fragment.get('access');
    const refresh = fragment.get('refresh');
    // The fragment is cleared at once so the tokens do not sit in the address bar or history.
    window.history.replaceState(null, '', '/sso/callback');
    if (access && refresh) {
      tokenStore.set({ accessToken: access, refreshToken: refresh } as never);
      void queryClient.invalidateQueries();
      navigate('/dashboard', { replace: true });
    } else navigate('/login?sso=failed', { replace: true });
  }, [navigate, queryClient]);
  return (
    <div className="flex min-h-screen items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}
